import { environmentManager, type QueryFunctionContext, queryOptions } from '@tanstack/react-query';

import type { WagerId } from '../../../schema/wager/entry.js';
import { type IDefinition, InternalError } from '../../domain/entry.js';
import { type ExtractActionReturnType, wagerQueryKeys, type WagerWorkerMessageMap } from '../../lib/entry.js';
import type { getWager } from '../actions/wager/getWager.js';

type WagerData = ExtractActionReturnType<typeof getWager>;

type PostCalculateFn = (
  type: 'totalNumberOfRows',
  payload: WagerWorkerMessageMap['totalNumberOfRows']['payload']
) => Promise<WagerWorkerMessageMap['totalNumberOfRows']['response']>;

const createFetcher =
  (post: PostCalculateFn, definition: IDefinition) =>
  ({
    queryKey: [{ wagerId }],
    client,
  }: QueryFunctionContext<ReturnType<(typeof wagerQueryKeys)['totalNumberOfRows']>>): Promise<
    WagerWorkerMessageMap['totalNumberOfRows']['response']
  > => {
    const wager = client.getQueryData<WagerData>(wagerQueryKeys.wager(wagerId));

    if (!wager) {
      // TODO: replace with actual error class
      throw new InternalError(`No wager for: ${wagerId}. Please fetch it using useWager`);
    }

    const couponWithKey = {
      // key will ensure calculation of srow is cached in worker
      key: wagerId,
      ...wager.coupon,
    } satisfies WagerWorkerMessageMap['totalNumberOfRows']['payload']['coupon'];

    return post('totalNumberOfRows', { coupon: couponWithKey, definition: definition.model });
  };

type QueryKeys = {
  wagerId: WagerId;
};

type QueryFunctionDependencies = {
  post: PostCalculateFn;
  definition: IDefinition;
};

/**
 * Returns query options for fetching the total number of rows for a given wager.
 *
 * @param root0 Query key options
 * @param root0.wagerId The ID of the wager to calculate total number of rows for
 * @param root1 Query function dependencies
 * @param root1.post Function to post calculation requests
 * @param root1.definition The product definition model
 * @returns Query options for use with TanStack React Query
 */
export function totalNumberOfRowsOptions(
  { wagerId }: QueryKeys,
  { post, definition }: QueryFunctionDependencies
): ReturnType<
  typeof queryOptions<
    WagerWorkerMessageMap['totalNumberOfRows']['response'],
    Error,
    WagerWorkerMessageMap['totalNumberOfRows']['response'],
    ReturnType<(typeof wagerQueryKeys)['totalNumberOfRows']>
  >
> {
  return queryOptions<
    WagerWorkerMessageMap['totalNumberOfRows']['response'],
    Error,
    WagerWorkerMessageMap['totalNumberOfRows']['response'],
    ReturnType<(typeof wagerQueryKeys)['totalNumberOfRows']>
  >({
    queryKey: wagerQueryKeys.totalNumberOfRows(wagerId),
    queryFn: createFetcher(post, definition),
    enabled: !environmentManager.isServer(),
  });
}
